import { HeaderPositionType, OrientationType, TextStyleType } from "../constants";
import { BaseCategory } from "./BaseCategory";
import { SlideCategories } from "./CategoryController";
import { SlideModelBuilder } from "./SlideModelBuilder";
import { uuid } from "../utils/uuid";

export class OrgChartCategory extends BaseCategory {
    get name() {
        return "Org Chart";
    }

    get type() {
        return SlideCategories.ORG_CHART;
    }

    get icon() {
        return null;
    }

    get description() {
        return "The content represents an organizational structure, hierarchy, or reporting relationships. Use this for company org charts, team structures, reporting chains, or any hierarchical relationships between people or roles.";
    }

    get slideTypes() {
        return [];
    }

    getPrompt() {
        return `Create an organizational chart showing hierarchical relationships.
        Provide nodes representing people, roles, or positions.
        Each node should have a name and optional title/description.
        Define parent-child relationships to establish the hierarchy.
        The root node (top of hierarchy) should have no parent.`;
    }

    getPromptSchema() {
        return {
            nodes: [{
                id: "unique identifier for the node",
                name: "the person's name or role title",
                title: "optional job title, role description, or department",
                parent: "id of the parent node (omit for root node)"
            }],
            conclusion: "an optional short conclusion or takeaway for the slide",
        };
    }

    validateGeneratedModel(model) {
        if (!this.validateArray(model.nodes)) {
            return false;
        }
        return true;
    }

    getSlideModels(model, options = {}) {
        let models = [
            this.buildOrgChart(model, options),
        ];

        return models;
    }

    buildOrgChart(model, options = {}) {
        let builder = new SlideModelBuilder("orgchart", model);
        builder.createHeader({ heading: model.slideTitle, position: HeaderPositionType.TOP });

        // Build items array from nodes
        const items = [];
        const nodeMap = {};

        // First pass: create all nodes and map them
        model.nodes.forEach((node, index) => {
            const nodeId = node.id || uuid();
            nodeMap[node.id] = nodeId;

            const item = {
                id: nodeId,
                nodeType: "person",
                text: {
                    blocks: [
                        builder.createBlock(node.name, TextStyleType.TITLE),
                        builder.createBlock(node.title, TextStyleType.BODY)
                    ].filter(b => b)
                }
            };

            items.push(item);
        });

        // Second pass: establish parent-child relationships and set indices
        model.nodes.forEach((node, index) => {
            const nodeId = nodeMap[node.id];
            const item = items.find(i => i.id === nodeId);

            if (node.parent && nodeMap[node.parent]) {
                item.parent = nodeMap[node.parent];

                // Calculate index among siblings
                const siblings = model.nodes.filter(n => n.parent === node.parent);
                const siblingIndex = siblings.findIndex(n => n.id === node.id);
                item.index = siblingIndex;
            } else {
                // Root node
                item.index = 0;
            }
        });

        builder.createPrimary({
            defaultNodeStyle: "box",
            hideRootNode: false,
            layout: "expanded",
            layoutDirection: OrientationType.VERTICAL,
            items: items
        });

        if (options.citeSources) {
            builder.createSourcesAttribution(model.sources);
        }

        if (model.conclusion) {
            builder.createBottomTray(model.conclusion);
        }

        return builder.model;
    }
}
